// src/components/dashboard/ProgressByLevel.js

import React, { useState, useEffect } from 'react';
import ChartHeader from '../common/ChartHeader';
import GlassyIcon from '../common/GlassyIcon';

// Custom hook to check for screen size
function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, [matches, query]);
  return matches;
}

// Modern circular progress indicator with theme awareness
const CircularProgress = ({ percentage, color, size = 40, isTotal = false }) => (
  <div className="relative" style={{ width: size, height: size }}>
    <svg className="transform -rotate-90" width={size} height={size}>
      {/* Background circle with theme awareness */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={(size - 6) / 2}
        stroke={isTotal ? 'rgba(255, 255, 255, 0.2)' : 'var(--color-border)'}
        strokeWidth="3"
        fill="none"
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={(size - 6) / 2}
        stroke={color}
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={`${2 * Math.PI * ((size - 6) / 2)}`}
        strokeDashoffset={`${2 * Math.PI * ((size - 6) / 2) * (1 - percentage / 100)}`}
        className="transition-all duration-700 ease-out"
        style={{
          filter: isTotal ? 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.3))' : `drop-shadow(0 0 4px ${color}30)`,
        }}
      />
    </svg>
    {/* Percentage text */}
    <div className="absolute inset-0 flex items-center justify-center">
      <span className={`text-xs font-semibold ${isTotal ? 'text-white' : 'text-content-primary'}`}>{percentage}%</span>
    </div>
  </div>
);

// Helper function to get glassy icon configuration for N-levels
const getLevelGlassyConfig = (level) => {
  const n = (level >= 0 && level <= 5) ? level : 0;
  return {
    gradient: `linear-gradient(to bottom right, var(--color-n-${n}), var(--color-n-${n}))`,
    shadow: `0 4px 6px -1px color-mix(in srgb, var(--color-n-${n}) 50%, transparent)`,
  };
};

// Simple icon component for level display
const LevelIcon = ({ children }) => <div className="flex items-center justify-center text-sm font-bold text-white">{children}</div>;

// Enhanced glassmorphic level card
const LevelCard = ({ label, count, total, percentage, color, isTotal = false, level = null }) => (
  <div
    className={`
      relative p-4 rounded-xl backdrop-blur-sm transition-all duration-300 overflow-hidden group
      animate-fade-in
      ${
        isTotal
          ? 'bg-gradient-to-br from-primary to-secondary text-white border-white/20 shadow-lg'
          : 'bg-background-secondary border-2 border-border' // Removed /70 opacity
      }
    `}
  >
    {/* Enhanced glassmorphic overlay - Removed opacity-50 */}
    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent transition-opacity duration-300" />

    {/* Colored background overlay for non-total cards */}
    {!isTotal && (
      <div
        className="absolute inset-0 opacity-5 bg-gradient-to-br"
        style={{
          background: `linear-gradient(135deg, ${color}20, ${color}10)`,
        }}
      />
    )}

    {/* Animated background pattern for total card */}
    {isTotal && (
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-24 h-24 bg-white rounded-full -translate-y-12 translate-x-12" />
        <div className="absolute bottom-0 left-0 w-16 h-16 bg-white rounded-full translate-y-8 -translate-x-8" />
      </div>
    )}

    {/* Subtle decorative elements for non-total cards */}
    {!isTotal && (
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute top-0 right-0 w-12 h-12 rounded-full"
          style={{
            backgroundColor: color,
            transform: 'translate(6px, -6px)',
          }}
        />
      </div>
    )}

    <div className="relative z-10 flex items-center gap-4">
      {/* Enhanced level indicator */}
      {isTotal ? (
        <div
          className={`
            flex items-center justify-center w-10 h-10 rounded-lg text-sm font-bold
            transition-all duration-300 backdrop-blur-sm border border-white/20
            bg-white/20 text-white shadow-md
          `}
        >
          {label}
        </div>
      ) : (
        <GlassyIcon
          icon={() => <LevelIcon>{label}</LevelIcon>}
          size="md"
          customGradient={getLevelGlassyConfig(level).gradient}
          customShadow={getLevelGlassyConfig(level).shadow}
        />
      )}

      {/* Progress info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-sm font-semibold truncate ${isTotal ? 'text-white' : 'text-content-primary'}`}>
            {isTotal ? 'Overall Progress' : `Level ${label}`}
          </span>
          <span className={`text-xs font-mono ml-2 ${isTotal ? 'text-white/80' : 'text-content-secondary'}`}>
            {count}/{total}
          </span>
        </div>

        {/* Enhanced progress bar */}
        <div
          className={`
            relative h-2 rounded-full overflow-hidden backdrop-blur-sm
            ${isTotal ? 'bg-white/20' : 'bg-background-primary/80 border border-border'}
          `}
        >
          <div
            className="absolute top-0 left-0 h-full rounded-full transition-all duration-700 ease-out"
            style={{
              width: `${percentage}%`,
              backgroundColor: color,
              boxShadow: `0 0 8px ${color}50`,
            }}
          >
            {/* Enhanced shimmer effect */}
            {!isTotal && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-pulse opacity-60" />}
          </div>
        </div>
      </div>

      {/* Circular progress indicator */}
      <CircularProgress percentage={percentage} color={isTotal ? 'white' : color} size={36} isTotal={isTotal} />
    </div>
  </div>
);

// Enhanced compact card for Mobile view
const CompactLevelCard = ({ label, count, total, color, isTotal = false, level = null }) => (
  <div
    className={`
      relative flex items-center justify-between p-3 rounded-lg backdrop-blur-sm overflow-hidden
      animate-fade-in
      ${
        isTotal ? 'bg-gradient-to-r from-primary to-secondary text-white border-white/20 shadow-lg' : 'bg-background-secondary border-2 border-border' // Removed /70 opacity
      }
    `}
  >
    {/* Glassmorphic overlay - Removed opacity-50 */}
    <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />

    {/* Colored background for non-total cards */}
    {!isTotal && (
      <div
        className="absolute inset-0 opacity-8"
        style={{
          background: `linear-gradient(135deg, ${color}15, ${color}05)`,
        }}
      />
    )}

    {/* Content */}
    <div className="relative z-10 flex items-center gap-3 w-full">
      {isTotal ? (
        <div
          className={`
            flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-md text-xs font-bold
            transition-all duration-300 backdrop-blur-sm border border-white/20
            bg-white/20 text-white
          `}
        >
          {label}
        </div>
      ) : (
        <div className="flex-shrink-0">
          <GlassyIcon
            icon={() => <LevelIcon>{label}</LevelIcon>}
            size="sm"
            customGradient={getLevelGlassyConfig(level).gradient}
            customShadow={getLevelGlassyConfig(level).shadow}
          />
        </div>
      )}
      <div className="flex flex-col items-end flex-1">
        <span className={`text-sm font-bold tabular-nums leading-tight ${isTotal ? 'text-white' : 'text-content-primary'}`}>
          {count}
        </span>
        <span className={`text-xs tabular-nums leading-tight ${isTotal ? 'text-white/60' : 'text-content-secondary'}`}>
          /{total}
        </span>
      </div>
    </div>
  </div>
);

const ProgressByLevel = ({ userStats, totalStats, type }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const levels = type === 'words' ? [5, 4, 3, 2, 1, 0] : [5, 4, 3, 2, 1];
  const learnedStats = userStats;
  const totalCounts = totalStats;

  const totalLearned = Object.values(learnedStats).reduce((sum, count) => sum + count, 0);
  const totalOverall = Object.values(totalCounts).reduce((sum, count) => sum + count, 0);
  const totalPercentage = totalOverall > 0 ? Math.round((100 * totalLearned) / totalOverall) : 0;

  return (
    <div className="h-full flex flex-col overflow-hidden rounded-2xl border-2 border-border bg-background-secondary/70 backdrop-blur-sm p-4">
      <ChartHeader title={`${type.charAt(0).toUpperCase() + type.slice(1)} Mastery`} className="min-h-[3.5rem] flex flex-col justify-center" />

      <div className="flex-1 flex flex-col">
        <div className="flex-1 space-y-3">
          {levels.map((level) => {
            const total = totalCounts?.[level] ?? 0;
            const learned = learnedStats?.[level] ?? 0;
            const percentage = total > 0 ? Math.round((100 * learned) / total) : 0;
            const levelLabel = `N${level}`;

            return isMobile ? (
              <CompactLevelCard key={level} label={levelLabel} count={learned} total={total} color={`var(--color-n-${level})`} level={level} />
            ) : (
              <LevelCard
                key={level}
                label={levelLabel}
                count={learned}
                total={total}
                percentage={percentage}
                color={`var(--color-n-${level})`}
                level={level}
              />
            );
          })}
        </div>

        <div className="pt-4 mt-4">
          {/* Enhanced divider with glassmorphic effect */}
          <div className="relative mb-4">
            <div className="w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent blur-sm" />
          </div>

          {isMobile ? (
            <CompactLevelCard isTotal label="ALL" count={totalLearned} total={totalOverall} percentage={totalPercentage} color="white" />
          ) : (
            <LevelCard isTotal label="ALL" count={totalLearned} total={totalOverall} percentage={totalPercentage} color="white" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgressByLevel;
